import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { LoginService } from '../pages/login/login.service';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { Meta } from '@angular/platform-browser';

export interface ThemeConfig {
  svgLight: string;
  svgDark: string;
  primaryColor?: string;
  buttonColor?: string;
  logoPath?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomThemeService {
  private loginRegisterLogoPathSubject = new BehaviorSubject<string>(undefined);
  loginRegisterLogoPath$ = this.loginRegisterLogoPathSubject.asObservable();

  private logoPathSubject = new BehaviorSubject<string>(
    undefined
  );

  logoPath$ = this.logoPathSubject.asObservable();

  private smallLogoPathSubject = new BehaviorSubject<string>(
    undefined
  );

  smallLogoPath$ = this.smallLogoPathSubject.asObservable();

  constructor(private login: LoginService, private meta: Meta) { }

  getCssVariable(name: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  }

  async applyTheme(theme: any) {
    if (typeof document === 'undefined') return;
  
    theme = this.getValidTheme(theme);
  
    const root = document.documentElement;
    const isWhiteLabel = theme?.isWhiteLableEnabled === true;
  
    this.applyColors(root, theme, isWhiteLabel);
  
    await this.applyLogos(theme, isWhiteLabel);
  
    await this.applyLoginBackground(root, theme, isWhiteLabel);
  
    await this.applyFavicon(theme, isWhiteLabel);
  
    this.applyMeta(theme, isWhiteLabel);
  }

  private getValidTheme(theme: any) {
    if (!theme || Object.keys(theme).length === 0) {
      return JSON.parse(localStorage.getItem('APP_THEME') || '{}');
    }
    return theme;
  }
  
  private applyColors(root: HTMLElement, theme: any, isWhiteLabel: boolean) {
    if (theme?.svgLight)
      root.style.setProperty('--svg-light-green', theme.svgLight);
  
    if (theme?.svgDark)
      root.style.setProperty('--svg-dark-green', theme.svgDark);
  
    if (theme?.primaryColor)
      root.style.setProperty('--primary-color', theme.primaryColor);
  
    if (isWhiteLabel && theme?.buttonColour) {
      root.style.setProperty('--button-color', theme.buttonColour);
      root.style.setProperty(
        '--hover-dark-button-color',
        this.darkenColor(theme.buttonColour, 25)
      );
    }
  
    if (isWhiteLabel && theme?.fieldLabelColour)
      root.style.setProperty('--green', theme.fieldLabelColour);
  
    if (isWhiteLabel && theme?.linkColours)
      root.style.setProperty('--link-green', theme.linkColours);
  
    if (isWhiteLabel && theme?.menuBackgroundColour)
      this.applyMenuColors(root, theme.menuBackgroundColour);
  
    if (isWhiteLabel && theme?.userIconColour)
      root.style.setProperty('--user-icon-color', theme.userIconColour);
  }

  private applyMenuColors(root: HTMLElement, bgColor: string) {
    root.style.setProperty('--menu-background-color', bgColor);
  
    const fontColor = this.getContrastTextColor(bgColor);
  
    root.style.setProperty('--menu-font-color', fontColor);
    root.style.setProperty('--menu-active-font', bgColor);
    root.style.setProperty('--menu-active-bg', fontColor);
  }

  private async applyLogos(theme: any, isWhiteLabel: boolean) {
    await this.setLogo(
      this.loginRegisterLogoPathSubject,
      theme,
      isWhiteLabel,
      theme?.logoImage,
      '/assets/images/yokahu-logo.png'
    );
  
    await this.setLogo(
      this.logoPathSubject,
      theme,
      isWhiteLabel,
      theme?.logoImage,
      '/assets/images/yokahu-logo-black.svg'
    );
  
    await this.setLogo(
      this.smallLogoPathSubject,
      theme,
      isWhiteLabel,
      theme?.collapsedLogo,
      '/assets/images/yokahu-logo-only.png'
    );
  }

  private async setLogo(
    subject: any,
    theme: any,
    isWhiteLabel: boolean,
    imageKey: string,
    fallback: string
  ) {
    subject.next(undefined);
  
    if (isWhiteLabel && imageKey) {
      const url = await this.safeFetchImage(theme?.orgId, imageKey);
      subject.next(url);
    } else {
      subject.next(fallback);
    }
  }

  private async applyLoginBackground(
    root: HTMLElement,
    theme: any,
    isWhiteLabel: boolean
  ) {
    const fallback = '/assets/images/trianglify-lowres.png';
  
    root.style.setProperty('--form-bg-image', 'none');
  
    if (isWhiteLabel && theme?.loginBackgroundImage) {
      const bgUrl = await this.safeFetchImage(
        theme?.orgId,
        theme?.loginBackgroundImage
      );
  
      if (bgUrl) {
        const isValid = await this.checkImage(bgUrl);
  
        if (isValid) {
          root.style.setProperty('--form-bg-image', `url("${bgUrl}")`);
          return;
        }
      }
    }
    
    root.style.setProperty('--form-bg-image', `url("${fallback}")`);
  }  

  private checkImage(url: string): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  }  

  private async applyFavicon(theme: any, isWhiteLabel: boolean) {
    const fallback = '/assets/images/favicon.svg';
  
    if (isWhiteLabel && theme?.faviconImage) {
      const faviconUrl = await this.safeFetchImage(
        theme?.orgId,
        theme?.faviconImage
      );
      this.setFavicon(faviconUrl || fallback);
    } else {
      this.setFavicon(fallback);
    }
  }

  private applyMeta(theme: any, isWhiteLabel: boolean) {
    if (isWhiteLabel && theme?.siteDescription) {
      this.meta.updateTag({
        name: 'description',
        content: theme.siteDescription
      });
    }
  }  

  private async safeFetchImage(orgId: string, key: string): Promise<string> {
    try {
      const response: any = await firstValueFrom(
        this.login.getWhiteLabelAssetPreviewUrl({ orgId, key }).pipe(
          timeout(8000),
          catchError(err => {
            console.warn('Theme asset fetch failed:', key, err);
            return of(null);
          })
        )
      );

      return response?.url || '';
    } catch (error) {
      console.warn('Theme asset fetch timeout or error:', key);
      return '';
    }
  }

  private darkenColor(hex: string, amount = 20): string {
    hex = hex.replace('#', '');

    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }

    const num = parseInt(hex, 16);

    let r = (num >> 16) - amount;
    let g = ((num >> 8) & 0x00ff) - amount;
    let b = (num & 0x0000ff) - amount;

    r = Math.max(0, r);
    g = Math.max(0, g);
    b = Math.max(0, b);

    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  }

  parseColor(color: string): { r: number; g: number; b: number; a?: number } | null {
    color = color.trim();

    // HEX
    if (color.startsWith('#')) {
      let hex = color.substring(1);
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
      }
      const num = parseInt(hex, 16);
      return {
        r: (num >> 16) & 255,
        g: (num >> 8) & 255,
        b: num & 255
      };
    }

    // RGB / RGBA
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
      return {
        r: +match[1],
        g: +match[2],
        b: +match[3],
        a: match[4] ? +match[4] : 1
      };
    }

    return null;
  }


  getContrastTextColor(bgColor: string): string {
    const parsed = this.parseColor(bgColor);
    if (!parsed) return '#333';

    let { r, g, b, a } = parsed;

    // Handle transparency (blend with white background)
    if (a !== undefined && a < 1) {
      r = Math.round(r * a + 255 * (1 - a));
      g = Math.round(g * a + 255 * (1 - a));
      b = Math.round(b * a + 255 * (1 - a));
    }

    const brightness =
      (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 150 ? '#333' : '#FFFFFF';
  }

  setFavicon(url: string) {
    if (!url || typeof document === 'undefined') return;

    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      document.head.appendChild(link);
    }

    link.href = url;
  }
}
